// tslint:disable: triple-equals
import React, { useEffect, useRef, useState } from 'react';
import {
  Controls,
  DisplayMetaData,
  DisplayName,
  VolumeContainer,
  Wrapper,
  AudioTimeStamp,
  Track,
} from './styled.audioPlayer';
import PrevPlay from '../../icons/PrevPlay';
import Play from '../../icons/Play';
import Pause from '../../icons/Pause';
import NextPlay from '../../icons/NextPlay';
import { Voice } from '../../icons/Voice';
import Mute from '../../icons/Mute';
import { IAudioProps } from '../../../@types/audio';
import { isIOS } from 'react-device-detect';
import { showErrorMessage } from '../../../../helpers/showMessage';
import { handleVisibilityChange } from '../../../../utils/audio';

const AudioPlayer = ({ urls, currentFile = 0 }: IAudioProps) => {
  const [currentTrack, setCurrentTrack] = useState<number>(currentFile);
  const audioRef = useRef(new Audio(urls && urls[currentTrack].url));
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [volume, setVolume] = useState<number>(0.5);
  const [currentTimer, setCurrentTimer] = useState<number>(0);
  const [duration, setDuration] = useState<number>(0);
  const [iosDevice, setIosDevice] = useState<boolean>(false);
  const [isTrackChanged, setIsTrackChanged] = useState<boolean>(false);

  useEffect(() => {
    const audio = audioRef.current;

    const updateTime = () => setCurrentTimer(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration);

    document.addEventListener('visibilitychange', handleVisibilityChange);
    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      if (!isTrackChanged) {
        setIsPlaying(false);
        audio.pause();
        audio.currentTime = 0;
      }
    };
  }, [currentTrack, document.hidden]);

  const playPauseTrack = () => {
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const playPrevTrack = () => {
    setIsTrackChanged(true);
    if (urls) {
      if (currentTrack == 0) {
        return setCurrentTrack(urls.length - 1);
      }
      setCurrentTrack((currentTrack - 1 + urls.length) % urls.length);
      audioRef.current.src =
        urls[(currentTrack - 1 + urls.length) % urls.length].url;
      if (isPlaying) {
        audioRef.current.play();
      }
    }
  };

  const seekTrack = (event) => {
    const audio = audioRef.current;
    audio.currentTime = event.target.value;
    setCurrentTimer(event.target.value);
  };

  const playNextTrack = () => {
    // TODO: refactor to work repeat music
    setIsTrackChanged(true);
    if (urls) {
      if (currentTrack == urls.length) {
        return setCurrentTrack(0);
      }
      setCurrentTrack((currentTrack + 1) % urls.length);
      audioRef.current.src = urls[(currentTrack + 1) % urls.length].url;
      if (isPlaying) {
        audioRef.current.play();
      }
    }
  };

  const adjustVolume = (event) => {
    const audio = audioRef.current;
    audio.volume = event.target.value;
    setVolume(event.target.value);
  };

  useEffect(() => {
    setIosDevice(isIOS);
    if (isIOS) {
      showErrorMessage(
        'On iOS device you have to use the iPhone volume controls'
      );
    }
  }, []);

  return (
    <Wrapper>
      <DisplayName>{urls && urls[currentTrack].name}</DisplayName>
      <DisplayMetaData>
        Uploaded: {urls && urls[currentTrack].date} -{' '}
        {Math.floor(duration / 60)}m{' '}
        {Math.floor(duration % 60)
          .toString()
          .padStart(2, '0')}
        sec
      </DisplayMetaData>

      {!iosDevice && (
        <VolumeContainer>
          <Track
            type="range"
            min="0"
            max="1"
            step="0.01"
            orient="vertical"
            value={volume}
            onChange={adjustVolume}
            style={{
              background: `linear-gradient(to right, #fff ${
                volume * 100
              }%, #458782 ${volume * 100}%)`,
            }}
          />
          <div onClick={() => (!!volume ? setVolume(0) : setVolume(0.5))}>
            {volume == 0 ? <Mute /> : <Voice />}
          </div>
        </VolumeContainer>
      )}
      <Controls>
        <span onClick={playPrevTrack}>
          <PrevPlay />
        </span>
        <span onClick={playPauseTrack}>{isPlaying ? <Pause /> : <Play />}</span>
        <span onClick={playNextTrack}>
          <NextPlay />
        </span>
      </Controls>

      <div>
        <Track
          type="range"
          min="0"
          max={duration}
          onChange={seekTrack}
          value={currentTimer}
          style={{
            background: `linear-gradient(to right, #fff ${
              (currentTimer / duration) * 100
            }%, #458782 ${(currentTimer / duration) * 100}%)`,
          }}
        />
        <AudioTimeStamp>
          <span>
            {Math.floor(currentTimer / 60)}:
            {Math.floor(currentTimer % 60)
              .toString()
              .padStart(2, '0')}
          </span>
          <span>
            {Math.floor(duration / 60)}:
            {Math.floor(duration % 60)
              .toString()
              .padStart(2, '0')}
          </span>
        </AudioTimeStamp>
      </div>
    </Wrapper>
  );
};

export default AudioPlayer;
